/**
 * The boot screen (index.html `#boot`, a fixed overlay beside `#root`) and its
 * loader, the mountain day cycle in `#boot-scene`. React never replaces it:
 * it stays over the app, one continuous loader, until the app calls
 * `hideBoot()` once its first frame is up (v2: V2App; current app: Router).
 * A failure (PageBoundary) hides it too, and so does a 15 s safety timeout,
 * so a hang never leaves it up.
 *
 * Dev builds only, to review it: `?holdBoot` keeps the boot screen up
 * (index.tsx skips rendering the app, no timeout), `&progress=0.4` previews
 * the determinate mode, and `&theme=light|dark` forces the theme.
 */
import {mountMountainLoader} from './mountainLoader'

const FADE_MS = 200
const SAFETY_MS = 15_000

const params = new URLSearchParams(window.location.search)
const hold = import.meta.env.DEV && params.has('holdBoot')
const preview = import.meta.env.DEV ? params.get('progress') : null
const forced = import.meta.env.DEV ? params.get('theme') : null
if (forced === 'light' || forced === 'dark') document.documentElement.dataset.bootTheme = forced
// The person's chosen theme (index.html's boot-theme script) or the forced one; else the system's.
const chosen = document.documentElement.dataset.bootTheme
const theme = chosen === 'light' || chosen === 'dark' ? chosen : 'auto'

const host = document.getElementById('boot-scene')
const loader = host
  ? mountMountainLoader(host, {theme, progress: preview === null ? null : Number(preview)})
  : null

let hidden = false

/** Fades the boot screen out and removes it (idempotent; none under `?holdBoot`). */
export const hideBoot = (): void => {
  if (hidden || hold) return
  hidden = true
  const boot = document.getElementById('boot')
  const remove = (): void => {
    loader?.destroy()
    boot?.remove()
  }
  if (!boot) {
    remove()
    return
  }
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (reduced) {
    remove()
    return
  }
  boot.classList.add('boot-hiding')
  window.setTimeout(remove, FADE_MS)
}

if (!hold) window.setTimeout(hideBoot, SAFETY_MS)
